import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  AlertCircle, Search, Loader2, ExternalLink, CheckCircle, XCircle,
  Shield, Globe, Hash, Mail, Bug, ChevronDown, ChevronUp,
  MapPin, Server, Activity, Lock, Eye, AlertTriangle, Copy, Clock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const OSINT_BACKEND = process.env.REACT_APP_BACKEND_URL;

// Service brand colors and icons
const SERVICE_META = {
  'VirusTotal':      { color: '#394EFF', bg: '#EEF0FF', label: 'VT' },
  'AbuseIPDB':       { color: '#EF4444', bg: '#FEF2F2', label: 'AB' },
  'AlienVault OTX':  { color: '#8B5CF6', bg: '#F5F3FF', label: 'OTX' },
  'Shodan':          { color: '#F97316', bg: '#FFF7ED', label: 'SH' },
  'Shodan InternetDB':{ color: '#F97316', bg: '#FFF7ED', label: 'SH' },
  'GreyNoise Community':{ color: '#10B981', bg: '#ECFDF5', label: 'GN' },
  'IP Geolocation':  { color: '#0EA5E9', bg: '#F0F9FF', label: 'GEO' },
  'Spamhaus ZEN':    { color: '#6366F1', bg: '#EEF2FF', label: 'SPM' },
  'MalwareBazaar':   { color: '#DC2626', bg: '#FEF2F2', label: 'MB' },
  'CIRCL Hashlookup':{ color: '#059669', bg: '#ECFDF5', label: 'CIRCL' },
  'NIST NVD':        { color: '#0369A1', bg: '#F0F9FF', label: 'NVD' },
  'URLScan.io':      { color: '#7C3AED', bg: '#F5F3FF', label: 'URL' },
  'URLhaus':         { color: '#B45309', bg: '#FFFBEB', label: 'UH' },
  'PhishTank':       { color: '#DC2626', bg: '#FEF2F2', label: 'PT' },
  'OpenPhish':       { color: '#B45309', bg: '#FFFBEB', label: 'OP' },
  'DNS Twist':       { color: '#0891B2', bg: '#ECFEFF', label: 'TWIST' },
  'BGPView':         { color: '#4F46E5', bg: '#EEF2FF', label: 'BGP' },
  'ThreatMiner':     { color: '#6D28D9', bg: '#F5F3FF', label: 'TM' },
  'Blocklist.de':    { color: '#9F1239', bg: '#FFF1F2', label: 'BL' },
  'StopForumSpam':   { color: '#9D174D', bg: '#FDF2F8', label: 'SFS' },
  'Feodo Tracker':   { color: '#991B1B', bg: '#FEF2F2', label: 'FT' },
  'CI Army':         { color: '#B45309', bg: '#FFFBEB', label: 'CIA' },
  'ThreatFox':       { color: '#B91C1C', bg: '#FEF2F2', label: 'TFX' },
  'CrowdSec':        { color: '#EA580C', bg: '#FFF7ED', label: 'CS' },
  'Hunter.io':       { color: '#D97706', bg: '#FFFBEB', label: 'HIO' },
  'HaveIBeenPwned':  { color: '#7C3AED', bg: '#F5F3FF', label: 'HIBP' },
  'EmailRep.io':     { color: '#0284C7', bg: '#F0F9FF', label: 'ER' },
};

const getServiceMeta = (name) => SERVICE_META[name] || { color: '#64748B', bg: '#F8FAFC', label: name.slice(0, 2).toUpperCase() };

const SEVERITY_COLORS = {
  high:    { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
  medium:  { text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
  low:     { text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' },
  clean:   { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  unknown: { text: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', badge: 'bg-slate-100 text-slate-600' },
};

const regexMap = [
  { type: 'IPv4', regex: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/, placeholder: '8.8.8.8' },
  { type: 'IPv6', regex: /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/, placeholder: '2001:0db8::1' },
  { type: 'MD5', regex: /^[a-f0-9]{32}$/i, placeholder: '5d41402abc4b2a76b9719d911017c592' },
  { type: 'SHA1', regex: /^[a-f0-9]{40}$/i, placeholder: 'aaf4c61ddcc5e8a2dabede0f3b482cd9aea9434d' },
  { type: 'SHA256', regex: /^[a-f0-9]{64}$/i, placeholder: '2c26b46b68ffc68ff99b453c1d30413413422d706483bfa0f98a5e886266e7ae' },
  { type: 'Domain', regex: /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i, placeholder: 'example.com' },
  { type: 'URL', regex: /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/, placeholder: 'https://example.com/path' },
  { type: 'Email', regex: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/, placeholder: 'user@example.com' },
  { type: 'CVE', regex: /^CVE-[0-9]{4}-[0-9]{4,}$/i, placeholder: 'CVE-2024-1234' }
];

const serviceMap = {
  'IPv4': [
    { name: 'Shodan InternetDB', endpoint: '/api/osint/ip/internetdb', pathParam: true, free: true },
    { name: 'GreyNoise Community', endpoint: '/api/osint/ip/greynoise', pathParam: true, free: true },
    { name: 'IP Geolocation', endpoint: '/api/osint/ip/geolocation', pathParam: true, free: true },
    { name: 'Spamhaus ZEN', endpoint: '/api/osint/ip/spamhaus', pathParam: true, free: true },
    { name: 'Blocklist.de', endpoint: '/api/osint/ip/blocklist_de', pathParam: true, free: true },
    { name: 'StopForumSpam', endpoint: '/api/osint/ip/stopforumspam', pathParam: true, free: true },
    { name: 'Feodo Tracker', endpoint: '/api/osint/ip/feodotracker', pathParam: true, free: true },
    { name: 'CI Army', endpoint: '/api/osint/ip/cinsarmy', pathParam: true, free: true },
    { name: 'ThreatMiner', endpoint: '/api/osint/ip/threatminer', pathParam: true, free: true },
    { name: 'BGPView', endpoint: '/api/osint/ip/bgpview', pathParam: true, free: true },
    { name: 'VirusTotal', endpoint: '/api/osint/ip/virustotal', param: 'ioc', apiKey: true },
    { name: 'AbuseIPDB', endpoint: '/api/osint/ip/abuseipdb', pathParam: true, apiKey: true },
    { name: 'Shodan', endpoint: '/api/osint/ip/shodan', param: 'ioc', apiKey: true },
    { name: 'AlienVault OTX', endpoint: '/api/osint/ip/alienvault', param: 'ioc', apiKey: true },
    { name: 'ThreatFox', endpoint: '/api/osint/ip/threatfox', pathParam: true, apiKey: true },
    { name: 'CrowdSec', endpoint: '/api/osint/ip/crowdsec', pathParam: true, apiKey: true },
  ],
  'IPv6': [
    { name: 'IP Geolocation', endpoint: '/api/osint/ip/geolocation', pathParam: true, free: true },
    { name: 'VirusTotal', endpoint: '/api/osint/ip/virustotal', param: 'ioc', apiKey: true },
    { name: 'AbuseIPDB', endpoint: '/api/osint/ip/abuseipdb', pathParam: true, apiKey: true },
  ],
  'Domain': [
    { name: 'DNS Twist', endpoint: '/api/osint/domain/dnstwist', pathParam: true, free: true },
    { name: 'ThreatMiner', endpoint: '/api/osint/domain/threatminer', pathParam: true, free: true },
    { name: 'URLScan.io', endpoint: '/api/osint/url/urlscanio', pathParam: true, free: true },
    { name: 'VirusTotal', endpoint: '/api/osint/domain/virustotal', param: 'ioc', apiKey: true },
    { name: 'Shodan', endpoint: '/api/osint/domain/shodan', param: 'ioc', apiKey: true },
    { name: 'AlienVault OTX', endpoint: '/api/osint/domain/alienvault', param: 'ioc', apiKey: true },
    { name: 'SafeBrowsing', endpoint: '/api/osint/domain/safebrowsing', param: 'ioc', apiKey: true },
  ],
  'URL': [
    { name: 'PhishTank', endpoint: '/api/osint/url/phishtank', pathParam: true, free: true },
    { name: 'OpenPhish', endpoint: '/api/osint/url/openphish', pathParam: true, free: true },
    { name: 'URLhaus', endpoint: '/api/osint/url/urlhaus', pathParam: true, free: true },
    { name: 'VirusTotal', endpoint: '/api/osint/url/virustotal', param: 'ioc', apiKey: true },
    { name: 'SafeBrowsing', endpoint: '/api/osint/url/safebrowsing', param: 'ioc', apiKey: true },
  ],
  'MD5': [
    { name: 'CIRCL Hashlookup', endpoint: '/api/osint/hash/circl', pathParam: true, free: true },
    { name: 'MalwareBazaar', endpoint: '/api/osint/hash/malwarebazaar', pathParam: true, free: true },
    { name: 'ThreatMiner', endpoint: '/api/osint/hash/threatminer', pathParam: true, free: true },
    { name: 'VirusTotal', endpoint: '/api/osint/hash/virustotal', param: 'ioc', apiKey: true },
    { name: 'AlienVault OTX', endpoint: '/api/osint/hash/alienvault', param: 'ioc', apiKey: true },
  ],
  'SHA1': [
    { name: 'CIRCL Hashlookup', endpoint: '/api/osint/hash/circl', pathParam: true, free: true },
    { name: 'MalwareBazaar', endpoint: '/api/osint/hash/malwarebazaar', pathParam: true, free: true },
    { name: 'ThreatMiner', endpoint: '/api/osint/hash/threatminer', pathParam: true, free: true },
    { name: 'VirusTotal', endpoint: '/api/osint/hash/virustotal', param: 'ioc', apiKey: true },
    { name: 'AlienVault OTX', endpoint: '/api/osint/hash/alienvault', param: 'ioc', apiKey: true },
  ],
  'SHA256': [
    { name: 'CIRCL Hashlookup', endpoint: '/api/osint/hash/circl', pathParam: true, free: true },
    { name: 'MalwareBazaar', endpoint: '/api/osint/hash/malwarebazaar', pathParam: true, free: true },
    { name: 'ThreatMiner', endpoint: '/api/osint/hash/threatminer', pathParam: true, free: true },
    { name: 'VirusTotal', endpoint: '/api/osint/hash/virustotal', param: 'ioc', apiKey: true },
    { name: 'AlienVault OTX', endpoint: '/api/osint/hash/alienvault', param: 'ioc', apiKey: true },
    { name: 'Maltiverse', endpoint: '/api/osint/hash/maltiverse', pathParam: true, apiKey: true },
  ],
  'Email': [
    { name: 'StopForumSpam', endpoint: '/api/osint/email/stopforumspam', pathParam: true, free: true },
    { name: 'ThreatMiner', endpoint: '/api/osint/email/threatminer', pathParam: true, free: true },
    { name: 'Hunter.io', endpoint: '/api/osint/email/hunterio', pathParam: true, apiKey: true },
    { name: 'EmailRep.io', endpoint: '/api/osint/email/emailrepio', pathParam: true, apiKey: true },
    { name: 'HaveIBeenPwned', endpoint: '/api/osint/email/haveibeenpwnd', pathParam: true, apiKey: true },
  ],
  'CVE': [
    { name: 'NIST NVD', endpoint: '/api/osint/cve/nist_nvd', pathParam: true, free: true },
  ]
};

// ─── Service-Specific Card Renderers ─────────────────────────────────────────

const KVRow = ({ label, value, className = '' }) => (
  <div className={`flex items-start justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0 ${className}`}>
    <span className="text-xs text-slate-500 shrink-0 w-32">{label}</span>
    <span className="text-xs text-slate-800 text-right font-medium">{value ?? '—'}</span>
  </div>
);

const ThreatPill = ({ listed, listedLabel = 'LISTED', cleanLabel = 'NOT LISTED' }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${listed ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
    {listed ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
    {listed ? listedLabel : cleanLabel}
  </span>
);

const DetectionBar = ({ malicious = 0, suspicious = 0, total = 70 }) => {
  const detected = malicious + suspicious;
  const pct = total > 0 ? Math.round((detected / total) * 100) : 0;
  const color = malicious > 10 ? 'bg-red-500' : malicious > 0 ? 'bg-orange-500' : 'bg-emerald-500';
  return (
    <div className="space-y-1.5 mt-2">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-700">{detected}/{total} engines detected</span>
        <span className={`font-bold ${malicious > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{pct}%</span>
      </div>
      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex gap-3 text-xs text-slate-500">
        <span className="text-red-600 font-medium">{malicious} malicious</span>
        <span className="text-orange-500 font-medium">{suspicious} suspicious</span>
        <span className="text-emerald-600 font-medium">{total - detected} clean</span>
      </div>
    </div>
  );
};

const renderVirusTotal = (data) => {
  if (!data?.data?.attributes) return <span className="text-xs text-slate-500">No data</span>;
  const a = data.data.attributes;
  const stats = a.last_analysis_stats || {};
  const total = Object.values(stats).reduce((s, v) => s + v, 0);
  return (
    <div className="space-y-3">
      <DetectionBar malicious={stats.malicious || 0} suspicious={stats.suspicious || 0} total={total || 70} />
      {a.reputation !== undefined && <KVRow label="Reputation" value={a.reputation} />}
      {a.country && <KVRow label="Country" value={a.country} />}
      {a.as_owner && <KVRow label="AS Owner" value={a.as_owner} />}
      {a.last_analysis_date && <KVRow label="Last Analysed" value={new Date(a.last_analysis_date * 1000).toLocaleDateString()} />}
      {a.categories && Object.values(a.categories).length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {[...new Set(Object.values(a.categories))].slice(0, 5).map((c, i) => (
            <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{c}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const renderAbuseIPDB = (data) => {
  if (!data?.data) return <span className="text-xs text-slate-500">No data</span>;
  const d = data.data;
  const score = d.abuseConfidenceScore || 0;
  const color = score > 50 ? 'text-red-600' : score > 20 ? 'text-orange-500' : 'text-emerald-600';
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 mb-2">
        <div className="relative w-12 h-12">
          <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#f1f5f9" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9155" fill="none" stroke={score > 50 ? '#ef4444' : score > 20 ? '#f97316' : '#10b981'} strokeWidth="3"
              strokeDasharray={`${score} ${100 - score}`} strokeLinecap="round" />
          </svg>
          <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${color}`}>{score}%</span>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-700">Abuse Confidence</p>
          <p className="text-xs text-slate-500">{d.totalReports || 0} reports</p>
        </div>
      </div>
      <KVRow label="Country" value={d.countryCode} />
      <KVRow label="ISP" value={d.isp} />
      {d.domain && <KVRow label="Domain" value={d.domain} />}
      {d.usageType && <KVRow label="Usage Type" value={d.usageType} />}
      <KVRow label="Whitelisted" value={d.isWhitelisted ? 'Yes' : 'No'} />
    </div>
  );
};

const renderAlienVault = (data) => {
  if (!data) return <span className="text-xs text-slate-500">No data</span>;
  const pulses = data.pulse_info?.count || data.pulse_count || 0;
  const malware = data.malware_families?.slice(0, 5) || [];
  const techniques = data.attack_ids?.slice(0, 6) || [];
  const indicator = data.indicator || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Pulses', value: pulses, color: pulses > 0 ? 'text-red-600' : 'text-emerald-600' },
          { label: 'Malware', value: malware.length, color: malware.length > 0 ? 'text-orange-500' : 'text-slate-500' },
          { label: 'ATT&CK', value: techniques.length, color: techniques.length > 0 ? 'text-purple-600' : 'text-slate-500' },
        ].map((m, i) => (
          <div key={i} className="bg-slate-50 rounded-lg p-2 text-center">
            <p className={`text-lg font-bold ${m.color}`}>{m.value}</p>
            <p className="text-xs text-slate-500">{m.label}</p>
          </div>
        ))}
      </div>
      {indicator.reputation !== undefined && <KVRow label="Reputation" value={indicator.reputation} />}
      {indicator.country_name && <KVRow label="Country" value={indicator.country_name} />}
      {indicator.asn && <KVRow label="ASN" value={indicator.asn} />}
      {malware.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-1 font-medium">Malware Families</p>
          <div className="flex flex-wrap gap-1">
            {malware.map((m, i) => <span key={i} className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs font-medium">{m.display_name || m}</span>)}
          </div>
        </div>
      )}
      {techniques.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-1 font-medium">ATT&CK Techniques</p>
          <div className="flex flex-wrap gap-1">
            {techniques.map((t, i) => <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">{t.id || t}</span>)}
          </div>
        </div>
      )}
    </div>
  );
};

const renderGreyNoise = (data) => (
  <div className="space-y-2">
    <div className="flex gap-2 flex-wrap">
      <ThreatPill listed={data.noise} listedLabel="Internet Scanner" cleanLabel="Not a Scanner" />
      {data.riot && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700"><CheckCircle className="w-3 h-3" />RIOT (Known Good)</span>}
    </div>
    {data.classification && <KVRow label="Classification" value={data.classification} />}
    {data.name && <KVRow label="Name" value={data.name} />}
    {data.message && <p className="text-xs text-slate-500 pt-1 italic">{data.message}</p>}
  </div>
);

const renderShodanInternetDB = (data) => {
  if (data.message) return <p className="text-xs text-slate-500 italic">{data.message}</p>;
  return (
    <div className="space-y-2">
      {data.ports?.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-1 font-medium">Open Ports ({data.ports.length})</p>
          <div className="flex flex-wrap gap-1">
            {data.ports.slice(0, 15).map((p, i) => <span key={i} className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded text-xs font-mono">{p}</span>)}
          </div>
        </div>
      )}
      {data.vulns?.length > 0 && (
        <div>
          <p className="text-xs text-red-600 mb-1 font-medium">CVEs ({data.vulns.length})</p>
          <div className="flex flex-wrap gap-1">
            {data.vulns.slice(0, 8).map((v, i) => <span key={i} className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs">{v}</span>)}
          </div>
        </div>
      )}
      {data.hostnames?.length > 0 && <KVRow label="Hostnames" value={data.hostnames.slice(0, 2).join(', ')} />}
      {data.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {data.tags.slice(0, 6).map((t, i) => <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{t}</span>)}
        </div>
      )}
    </div>
  );
};

const renderGeoIP = (data) => (
  <div className="space-y-1">
    <div className="flex items-center gap-2 mb-2">
      <MapPin className="w-4 h-4 text-sky-500" />
      <span className="text-sm font-medium text-slate-700">{data.city || 'Unknown'}, {data.regionName || ''} {data.countryCode || ''}</span>
    </div>
    {data.isp && <KVRow label="ISP" value={data.isp} />}
    {data.org && <KVRow label="Organization" value={data.org} />}
    {data.as && <KVRow label="AS" value={data.as} />}
    {data.timezone && <KVRow label="Timezone" value={data.timezone} />}
    <div className="flex gap-3 pt-1">
      <ThreatPill listed={data.proxy} listedLabel="Proxy" cleanLabel="No Proxy" />
      <ThreatPill listed={data.hosting} listedLabel="Hosting" cleanLabel="Not Hosting" />
    </div>
  </div>
);

const renderSpamhaus = (data) => (
  <div className="space-y-2">
    <ThreatPill listed={data.listed} listedLabel={`LISTED: ${data.list_type || 'Spamhaus'}`} />
    {data.response && <KVRow label="Response" value={data.response} />}
  </div>
);

const renderURLScan = (data) => {
  if (!data) return <span className="text-xs text-slate-500">No data</span>;
  // URLScan returns results list or a single scan result
  const results = data.results || (Array.isArray(data) ? data : null);
  if (results?.length > 0) {
    const r = results[0];
    const verdict = r.verdicts?.overall;
    const page = r.page;
    return (
      <div className="space-y-2">
        {verdict && (
          <div className="flex items-center gap-2">
            <ThreatPill listed={verdict.malicious || verdict.score > 50}
              listedLabel={`MALICIOUS (score: ${verdict.score})`}
              cleanLabel={`CLEAN (score: ${verdict.score || 0})`} />
          </div>
        )}
        {page?.domain && <KVRow label="Domain" value={page.domain} />}
        {page?.country && <KVRow label="Country" value={page.country} />}
        {page?.ip && <KVRow label="IP" value={page.ip} />}
        {page?.server && <KVRow label="Server" value={page.server} />}
        {r.stats?.malicious > 0 && <KVRow label="Malicious requests" value={r.stats.malicious} />}
        {r.stats?.unencrypted > 0 && <KVRow label="Unencrypted requests" value={r.stats.unencrypted} />}
        {verdict?.categories?.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {verdict.categories.slice(0, 5).map((c, i) => (
              <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{c}</span>
            ))}
          </div>
        )}
      </div>
    );
  }
  // URLhaus-style or generic response
  if (data.url_status || data.threat) {
    return (
      <div className="space-y-2">
        <ThreatPill listed={data.url_status === 'online' || data.threat === 'malware_download'}
          listedLabel={`ACTIVE: ${data.threat || data.url_status}`} cleanLabel="OFFLINE/CLEAN" />
        {data.threat && <KVRow label="Threat" value={data.threat} />}
        {data.urlhaus_reference && <KVRow label="URLhaus Ref" value={data.urlhaus_reference} />}
      </div>
    );
  }
  return renderGeneric(data);
};

const renderBlocklist = (data, name) => {
  const isListed = data.found || data.listed || data.attacks > 0 || data.reports > 0;
  return (
    <div className="space-y-2">
      <ThreatPill listed={isListed} listedLabel="Found in Threat List" />
      {data.attacks > 0 && <KVRow label="Attacks" value={data.attacks} />}
      {data.reports > 0 && <KVRow label="Reports" value={data.reports} />}
      {data.malware && <KVRow label="Malware" value={data.malware} />}
      {data.reason && <KVRow label="Reason" value={data.reason} />}
      {data.description && <p className="text-xs text-slate-500">{data.description}</p>}
    </div>
  );
};

const renderMalwareBazaar = (data) => {
  if (data.query_status === 'hash_not_found')
    return <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-xs text-emerald-700 font-medium">Hash not found in malware database</span></div>;
  if (data.data?.[0]) {
    const s = data.data[0];
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-500" /><span className="text-xs font-semibold text-red-700">Malware Sample Found</span></div>
        <KVRow label="File Type" value={s.file_type} />
        {s.signature && <KVRow label="Signature" value={s.signature} />}
        {s.file_size && <KVRow label="File Size" value={`${s.file_size} bytes`} />}
        <KVRow label="First Seen" value={s.first_seen} />
        {s.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {s.tags.slice(0, 6).map((t, i) => <span key={i} className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs">{t}</span>)}
          </div>
        )}
      </div>
    );
  }
  return <span className="text-xs text-slate-500">No data</span>;
};

const renderCIRCL = (data) => {
  if (data.found === false)
    return <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /><span className="text-xs text-amber-700">Hash not in known-good database (may be unknown or malicious)</span></div>;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 mb-2"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-xs font-semibold text-emerald-700">Known Good File</span></div>
      {data.FileName && <KVRow label="Filename" value={data.FileName} />}
      {data.FileSize && <KVRow label="Size" value={`${data.FileSize} bytes`} />}
      {data.source && <KVRow label="Source" value={data.source} />}
    </div>
  );
};

const renderNVD = (data) => {
  if (!data?.vulnerabilities?.[0]?.cve) return <span className="text-xs text-slate-500">CVE not found</span>;
  const cve = data.vulnerabilities[0].cve;
  const desc = cve.descriptions?.[0]?.value;
  const cvss = cve.metrics?.cvssMetricV31?.[0]?.cvssData || cve.metrics?.cvssMetricV2?.[0]?.cvssData;
  return (
    <div className="space-y-2">
      {cvss && (
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-2xl font-bold ${cvss.baseScore >= 9 ? 'text-red-600' : cvss.baseScore >= 7 ? 'text-orange-500' : cvss.baseScore >= 4 ? 'text-amber-500' : 'text-blue-500'}`}>{cvss.baseScore}</span>
          <div>
            <p className="text-xs font-semibold text-slate-700">{cvss.baseSeverity || 'CVSS Score'}</p>
            <p className="text-xs text-slate-500 font-mono">{cvss.vectorString?.slice(0, 30)}...</p>
          </div>
        </div>
      )}
      {desc && <p className="text-xs text-slate-600 leading-relaxed">{desc.slice(0, 300)}{desc.length > 300 ? '...' : ''}</p>}
    </div>
  );
};

const renderDNSTwist = (data) => (
  <div className="space-y-2">
    <KVRow label="Domain" value={data.domain} />
    {data.permutations?.length > 0 && (
      <div>
        <p className="text-xs text-slate-500 mb-1 font-medium">Typosquatting Variants ({data.permutations.length})</p>
        <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
          {data.permutations.slice(0, 20).map((p, i) => <span key={i} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">{p}</span>)}
        </div>
      </div>
    )}
  </div>
);

const renderPhishTank = (data, name) => {
  const isPhishing = data.found || data.results?.in_database;
  return (
    <div className="space-y-2">
      <ThreatPill listed={isPhishing} listedLabel="PHISHING URL" cleanLabel="Not in Database" />
      {data.feed_size && <p className="text-xs text-slate-500">Feed: {data.feed_size.toLocaleString()} URLs</p>}
    </div>
  );
};

const renderThreatMiner = (data) => {
  if (!data?.results?.length) return <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-xs text-emerald-700">No threat intelligence found</span></div>;
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-slate-700">{data.results.length} result{data.results.length !== 1 ? 's' : ''} found</p>
      {data.results.slice(0, 3).map((r, i) => (
        <p key={i} className="text-xs text-slate-500 truncate">{typeof r === 'string' ? r : JSON.stringify(r).slice(0, 80)}</p>
      ))}
    </div>
  );
};

const renderBGPView = (data) => (
  <div className="space-y-1">
    {data.ip && <KVRow label="IP" value={data.ip} />}
    {data.rir_allocation?.rir_name && <KVRow label="RIR" value={data.rir_allocation.rir_name} />}
    {data.asns?.[0] && (
      <>
        <KVRow label="ASN" value={`AS${data.asns[0].asn}`} />
        {data.asns[0].name && <KVRow label="ASN Name" value={data.asns[0].name} />}
        {data.asns[0].country_code && <KVRow label="Country" value={data.asns[0].country_code} />}
      </>
    )}
  </div>
);

const renderGeneric = (data) => (
  <pre className="text-xs text-slate-500 bg-slate-50 rounded p-2 overflow-auto max-h-32 whitespace-pre-wrap">
    {JSON.stringify(data, null, 2).slice(0, 400)}{JSON.stringify(data).length > 400 ? '\n...' : ''}
  </pre>
);

function renderServiceData(service) {
  if (service.status === 'error' || service.error) {
    const errorStr = typeof service.error === 'string' ? service.error : String(service.error ?? '');
    const isApiKey = errorStr.includes('API') || errorStr.includes('api') || errorStr.includes('key') || errorStr.includes('403') || errorStr.includes('401');
    return (
      <div className="flex items-start gap-2">
        {isApiKey
          ? <><Lock className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" /><span className="text-xs text-slate-400">API key required to access this source</span></>
          : <><XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" /><span className="text-xs text-red-500">{errorStr.slice(0, 120) || 'Service unavailable'}</span></>
        }
      </div>
    );
  }
  const d = service.data;
  if (!d) return <span className="text-xs text-slate-400">No data returned</span>;

  switch (service.name) {
    case 'VirusTotal': return renderVirusTotal(d);
    case 'AbuseIPDB': return renderAbuseIPDB(d);
    case 'AlienVault OTX': return renderAlienVault(d);
    case 'GreyNoise Community': return renderGreyNoise(d);
    case 'Shodan InternetDB': return renderShodanInternetDB(d);
    case 'IP Geolocation': return renderGeoIP(d);
    case 'Spamhaus ZEN': return renderSpamhaus(d);
    case 'MalwareBazaar': return renderMalwareBazaar(d);
    case 'CIRCL Hashlookup': return renderCIRCL(d);
    case 'NIST NVD': return renderNVD(d);
    case 'DNS Twist': return renderDNSTwist(d);
    case 'BGPView': return renderBGPView(d);
    case 'URLScan.io': return renderURLScan(d);
    case 'ThreatMiner': return renderThreatMiner(d);
    case 'Blocklist.de': case 'StopForumSpam': case 'CI Army': case 'Feodo Tracker': case 'SSL Blacklist': case 'Emerging Threats': case 'FireHOL': case 'ThreatFox': case 'CrowdSec': return renderBlocklist(d, service.name);
    default: return renderGeneric(d);
  }
}

// ─── Threat Assessment Logic ─────────────────────────────────────────────────
function computeOverallThreat(services) {
  let threatScore = 0;
  services.forEach(s => {
    if (s.status === 'error') return;
    const d = s.data;
    if (!d) return;
    if (s.name === 'VirusTotal' && d.data?.attributes?.last_analysis_stats?.malicious > 0) threatScore += 3;
    if (s.name === 'AbuseIPDB' && (d.data?.abuseConfidenceScore || 0) > 25) threatScore += 2;
    if (s.name === 'AlienVault OTX' && (d.pulse_info?.count || 0) > 0) threatScore += 2;
    if (s.name === 'GreyNoise Community' && d.noise) threatScore += 1;
    if (s.name === 'Spamhaus ZEN' && d.listed) threatScore += 2;
    if (['Blocklist.de', 'Feodo Tracker', 'CI Army', 'ThreatFox', 'CrowdSec'].includes(s.name) && (d.found || d.listed || d.attacks > 0)) threatScore += 2;
    if (s.name === 'MalwareBazaar' && d.query_status !== 'hash_not_found' && d.data?.length > 0) threatScore += 3;
    if ((s.name === 'PhishTank' || s.name === 'OpenPhish') && (d.found || d.results?.in_database)) threatScore += 3;
    if (s.name === 'NIST NVD' && d.vulnerabilities?.[0]) threatScore += 1;
  });
  if (threatScore >= 5) return 'high';
  if (threatScore >= 2) return 'medium';
  const hasData = services.some(s => s.status === 'success' && s.data && !s.error);
  return hasData ? 'clean' : 'unknown';
}

// ─── Service Card Component ───────────────────────────────────────────────────
const ServiceCard = ({ service }) => {
  const [expanded, setExpanded] = useState(true);
  const meta = getServiceMeta(service.name);
  const hasData = service.status === 'success' && service.data && !service.error;
  const isError = service.status === 'error' || service.error;
  const errorStr = isError ? (typeof service.error === 'string' ? service.error : String(service.error ?? '')) : '';
  const isApiKey = isError && (errorStr.includes('API') || errorStr.includes('403') || errorStr.includes('401') || errorStr.includes('key'));

  return (
    <div className={`rounded-xl border transition-all ${isApiKey ? 'border-slate-100 opacity-60' : isError ? 'border-red-100' : 'border-slate-200 hover:border-slate-300'} bg-white`}>
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-white text-xs font-bold shrink-0"
            style={{ backgroundColor: meta.color }}
          >
            {meta.label}
          </span>
          <div>
            <span className="text-sm font-semibold text-slate-800">{service.name}</span>
            {isApiKey && <span className="ml-2 text-xs text-slate-400">Key required</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasData
            ? <CheckCircle className="w-4 h-4 text-emerald-500" />
            : isApiKey
              ? <Lock className="w-4 h-4 text-slate-300" />
              : <XCircle className="w-4 h-4 text-red-400" />
          }
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-50">
          {renderServiceData(service)}
        </div>
      )}
    </div>
  );
};

// ─── Threat Summary Banner ────────────────────────────────────────────────────
const ThreatBanner = ({ ioc, iocType, threatLevel, services, timestamp }) => {
  const levels = {
    high:    { label: 'HIGH RISK', icon: <AlertTriangle className="w-6 h-6" />, gradient: 'from-red-600 to-red-700', text: 'text-white', sub: 'text-red-100' },
    medium:  { label: 'MEDIUM RISK', icon: <AlertCircle className="w-6 h-6" />, gradient: 'from-orange-500 to-orange-600', text: 'text-white', sub: 'text-orange-100' },
    clean:   { label: 'NO THREAT', icon: <CheckCircle className="w-6 h-6" />, gradient: 'from-emerald-600 to-emerald-700', text: 'text-white', sub: 'text-emerald-100' },
    unknown: { label: 'UNKNOWN', icon: <Shield className="w-6 h-6" />, gradient: 'from-slate-500 to-slate-600', text: 'text-white', sub: 'text-slate-300' },
  };
  const l = levels[threatLevel] || levels.unknown;
  const successful = services.filter(s => s.status === 'success' && !s.error).length;

  const copyIOC = () => {
    navigator.clipboard.writeText(ioc);
    toast.success('IOC copied to clipboard');
  };

  return (
    <div className={`rounded-2xl bg-gradient-to-r ${l.gradient} p-5 text-white mb-6`} data-testid="threat-banner">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          {l.icon}
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wider ${l.sub} mb-0.5`}>Threat Assessment</p>
            <h3 className={`text-2xl font-bold ${l.text}`}>{l.label}</h3>
          </div>
        </div>
        <button onClick={copyIOC} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition" title="Copy IOC">
          <Copy className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'IOC', value: ioc.length > 28 ? ioc.slice(0, 28) + '…' : ioc },
          { label: 'Type', value: iocType },
          { label: 'Sources Queried', value: services.length },
          { label: 'Data Retrieved', value: `${successful}/${services.length}` },
        ].map((m, i) => (
          <div key={i} className="bg-white/10 rounded-lg px-3 py-2">
            <p className={`text-xs ${l.sub}`}>{m.label}</p>
            <p className="text-sm font-bold mt-0.5 font-mono">{m.value}</p>
          </div>
        ))}
      </div>
      {timestamp && (
        <p className={`mt-3 text-xs ${l.sub} flex items-center gap-1`}>
          <Clock className="w-3 h-3" />
          Analysed {new Date(timestamp).toLocaleString()}
        </p>
      )}
    </div>
  );
};

// ─── IOC Type Icon ────────────────────────────────────────────────────────────
const getTypeIcon = (type) => {
  const map = { IPv4: Globe, IPv6: Globe, Domain: Globe, URL: ExternalLink, Email: Mail, CVE: Bug, MD5: Hash, SHA1: Hash, SHA256: Hash };
  const Icon = map[type] || Shield;
  return <Icon className="w-4 h-4" />;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const IOCAnalyzer = () => {
  const [iocType, setIocType] = useState('');
  const [invalidInput, setInvalidInput] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [activeServices, setActiveServices] = useState([]);
  const [results, setResults] = useState(null);
  const inputRef = useRef(null);
  const [filterFreeOnly, setFilterFreeOnly] = useState(false);
  const [filterWithData, setFilterWithData] = useState(false);

  const validateIoc = useCallback((ioc) => {
    const trimmed = ioc.trim();
    for (const { type, regex } of regexMap) {
      if (regex.test(trimmed)) {
        setInvalidInput(false);
        setIocType(type);
        runAnalysis(trimmed, type);
        return true;
      }
    }
    setInvalidInput(true);
    return false;
  }, []);

  const runAnalysis = async (ioc, type) => {
    setAnalyzing(true);
    setResults(null);
    setActiveServices([]);

    const services = serviceMap[type] || [];
    const serviceResults = [];

    for (const service of services) {
      setActiveServices(prev => [...prev, service.name]);
      try {
        const url = service.pathParam
          ? `${OSINT_BACKEND}${service.endpoint}/${encodeURIComponent(ioc)}`
          : `${OSINT_BACKEND}${service.endpoint}?${service.param}=${encodeURIComponent(ioc)}`;
        const response = await axios.get(url, { timeout: 30000 });
        serviceResults.push({ name: service.name, status: 'success', data: response.data, error: response.data?.error != null ? String(response.data.error) : null });
      } catch (err) {
        serviceResults.push({ name: service.name, status: 'error', error: err.response?.data?.detail || err.message, data: null });
      }
    }

    setResults({ ioc, type, services: serviceResults, timestamp: new Date().toISOString() });
    setAnalyzing(false);
    toast.success('Analysis complete');
  };

  const handleAnalyze = () => {
    const val = inputRef.current?.value?.trim() || '';
    if (!val) { toast.error('Please enter an IOC'); return; }
    if (!validateIoc(val)) inputRef.current.value = '';
  };

  const filteredServices = results?.services?.filter(s => {
    if (filterFreeOnly) {
      const cfg = serviceMap[results.type]?.find(x => x.name === s.name);
      if (!cfg?.free) return false;
    }
    if (filterWithData && (s.status === 'error' || !s.data || s.error)) return false;
    return true;
  }) || [];

  const threatLevel = results ? computeOverallThreat(results.services) : null;

  const IOC_TYPES = ['IPv4', 'IPv6', 'Domain', 'URL', 'MD5', 'SHA1', 'SHA256', 'Email', 'CVE'];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold mb-1" data-testid="ioc-analyzer-title">IOC Analyzer</h2>
        <p className="text-muted-foreground text-sm">Multi-source threat intelligence for indicators of compromise</p>
      </div>

      {/* Search Bar */}
      <Card className="mb-6 shadow-sm">
        <CardContent className="pt-5">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              placeholder="Enter IPv4, domain, URL, file hash (MD5/SHA1/SHA256), email, or CVE..."
              onKeyDown={e => e.key === 'Enter' && handleAnalyze()}
              className="flex-1 h-11"
              data-testid="ioc-input"
            />
            <Button onClick={handleAnalyze} disabled={analyzing} className="h-11 px-5" data-testid="analyze-ioc-btn">
              {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Analyze
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {IOC_TYPES.map(t => (
              <button
                key={t}
                onClick={() => { if (inputRef.current) inputRef.current.focus(); }}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition ${
                  iocType === t
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {getTypeIcon(t)} {t}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Invalid Input */}
      {invalidInput && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Input doesn't match any supported IOC format. Supported: IPv4, IPv6, Domain, URL, Email, MD5, SHA1, SHA256, CVE-YYYY-NNNN</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {analyzing && (
        <Card className="mb-4">
          <CardContent className="py-6">
            <div className="flex items-center gap-3 mb-4">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
              <div>
                <p className="font-semibold text-sm">Querying threat intelligence sources...</p>
                <p className="text-xs text-muted-foreground">{activeServices.length} sources checked so far</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeServices.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700">
                  <CheckCircle className="w-3 h-3" />{s}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && !analyzing && (
        <div data-testid="ioc-results">
          {/* Threat Banner */}
          <ThreatBanner
            ioc={results.ioc}
            iocType={results.type}
            threatLevel={threatLevel}
            services={results.services}
            timestamp={results.timestamp}
          />

          {/* Filter Controls */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm font-medium text-slate-600">{filteredServices.length} source{filteredServices.length !== 1 ? 's' : ''}</span>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => setFilterFreeOnly(!filterFreeOnly)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition border ${filterFreeOnly ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              >
                Free Only
              </button>
              <button
                onClick={() => setFilterWithData(!filterWithData)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition border ${filterWithData ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              >
                With Data Only
              </button>
            </div>
          </div>

          {/* Service Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="service-cards">
            {filteredServices.map((service, idx) => (
              <ServiceCard key={idx} service={service} />
            ))}
          </div>

          {filteredServices.length === 0 && (
            <div className="text-center py-10 text-slate-400 text-sm">
              No results match current filters.
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!results && !analyzing && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-slate-400" />
          </div>
          <p className="text-slate-600 font-medium mb-1">Enter an IOC to start analysis</p>
          <p className="text-sm text-slate-400">Configure API keys in Settings to unlock all threat sources</p>
        </div>
      )}
    </div>
  );
};

export default IOCAnalyzer;
